// utils/expandAssembly.js
//
// An existing material's build-up (GET /breakdown/:id + GET /line-items/:id) → Build New Material rows.
//
//   • Load Template (no `block`): the template's rows replace the page's, keeping the source line-item ids
//     (calculator links resolve against them).
//   • Add Assembly (`block`): the assembly's rows are COPIED IN, scaled by "how much of it per 1 unit of
//     the new material", under a heading of its own — so a user can combine several approved assemblies
//     (plus products) into one. Nesting a material inside another can't be stored (every line item must
//     be a product/EPD rate), so a copy is what keeps approval, Nezo's totals and its breakdown working.
//     Every row in the block carries `assemblyBlock` so the whole block can be removed or rescaled later.

import { normKey, evaluateCell, recalcRowTotal } from './quantityLinks';
import { typeMatchesBase, TYPE_BASE } from './copyRequest';
import { multiplyFactor, fmt } from './conversionRecipes';

const descOf = (d) => String(d.CostMeasurementName || d.QuantityUnitName || d.Name || '').trim();

const parseRows = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string' || !raw.trim()) return [];
  try { const v = JSON.parse(raw); return Array.isArray(v) ? v : []; } catch { return []; }
};

const NUMERIC = ['count', 'length', 'width', 'height', 'factor'];

/** A calculator row with its evaluated cells and total refreshed (as the calculator itself does). */
const materialise = (row) => {
  const out = { ...row };
  NUMERIC.forEach(f => { out[`${f}_evaluated`] = evaluateCell(out[f]); });
  out.total = recalcRowTotal(out);
  return out;
};

const blankPack = () => ({ measurementId: '', code: '', unit: '', value: 0, total: 0 });

const separator = (uuid, isHeading, heading, subHeading, extra) => ({
  id: uuid(),
  isHeading,
  isSubHeading: !isHeading,
  heading,
  subHeading: isHeading ? null : subHeading,
  category: '',
  description: '',
  quantity: 0,
  unit: '',
  carbon: {},
  bio: {},
  weight: {},
  locked: false,
  ...extra,
});

/** The heading text for an added assembly: "Framing 90×45 × 2". */
export const blockTitle = (name, scale) => `${name || 'Assembly'} × ${fmt(Number(scale) || 0)}`;

/**
 * Build-up → { rows, costRows, rowHistory }.
 *
 * @param breakdown   rows of GET /breakdown/:id (one per factor per metric)
 * @param lineItems   rows of GET /line-items/:id (headings, sub_heading, sort_order, line_item_rows)
 * @param scale       multiplier for every quantity (Add Assembly's "how much per 1 unit")
 * @param block       { blockId, id, name } — set for Add Assembly; omit for Load Template
 * @param uuid        id generator
 * @param categoryFor desc → category (the page's findCategoryForDesc)
 */
export function breakdownToRows({ breakdown, lineItems, scale = 1, block = null, uuid, categoryFor = () => '' }) {
  const data = Array.isArray(breakdown) ? breakdown : [];
  const items = Array.isArray(lineItems) ? lineItems : [];
  const s = Number(scale);
  const k = Number.isFinite(s) && s > 0 ? s : 1;
  const liById = new Map(items.map(li => [li.id, li]));
  const assemblyBlock = block ? { blockId: block.blockId, id: block.id, name: block.name, scale: k } : null;
  const title = block ? blockTitle(block.name, k) : null;

  const placeOf = (d) => {
    const li = liById.get(d.FactorId);
    return { li, heading: li?.headings || null, subHeading: li?.sub_heading || null };
  };
  // inside a block, the source's headings become sub-headings ("Heading › Sub" when it had both)
  const blockSub = (h, sh) => (h ? (sh && String(sh).trim() ? `${h} › ${sh}` : h) : (sh || null));

  // ── environmental rows: one per heading::sub-heading::description (Carbon / Biogenic / Weight on one row) ──
  const grouped = new Map();
  for (const d of data) {
    if (typeMatchesBase(d.MeasurementType, TYPE_BASE.RATE)) continue;
    const desc = descOf(d);
    if (!desc) continue;
    const { li, heading, subHeading } = placeOf(d);
    const key = `${heading || ''}::${subHeading || ''}::${desc}`;
    if (!grouped.has(key)) {
      grouped.set(key, {
        desc, heading, subHeading,
        factorId: d.FactorId || '',
        lineItemId: li?.id || null,
        sortOrder: li?.sort_order !== undefined && li?.sort_order !== null ? Number(li.sort_order) : 9999,
        baseQty: Number(d.Factor || 0),
        unit: d.QuantityTypeName || d.Unit || '',
        carbon: blankPack(), bio: blankPack(), weight: blankPack(),
      });
    }
    const g = grouped.get(key);
    if (!g.lineItemId && li?.id) g.lineItemId = li.id;
    const pack = {
      measurementId: d.MeasurementType || '',
      code: d.MeasurementUnitRateId || '',
      unit: d.QuantityTypeName || '',
      value: Number(d.Value || 0),
    };
    if (typeMatchesBase(d.MeasurementType, TYPE_BASE.BIO)) g.bio = pack;
    else if (typeMatchesBase(d.MeasurementType, TYPE_BASE.CO2)) { g.carbon = pack; g.baseQty = Number(d.Factor || g.baseQty || 0); }
    else if (typeMatchesBase(d.MeasurementType, TYPE_BASE.WEIGHT)) g.weight = pack;
  }

  const groups = Array.from(grouped.values()).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

  const rows = [];
  const rowHistory = {};
  const envByKey = new Map();
  if (block) rows.push(separator(uuid, true, title, null, { fromAssembly: assemblyBlock, assemblyBlock }));

  let currentHeading = block ? title : null;
  let currentSub = null;
  for (const g of groups) {
    const heading = block ? title : g.heading;
    const subHeading = block ? blockSub(g.heading, g.subHeading) : g.subHeading;

    if (!block && heading && heading !== currentHeading) {
      currentHeading = heading;
      currentSub = null;
      rows.push(separator(uuid, true, heading, null));
    }
    if (subHeading && String(subHeading).trim() !== '' && subHeading !== currentSub) {
      currentSub = subHeading;
      rows.push(separator(uuid, false, currentHeading, subHeading, assemblyBlock ? { assemblyBlock } : {}));
    }

    const quantity = g.baseQty * k;
    const withTotal = (p) => (p.code || p.measurementId ? { ...p, total: (Number(p.value) || 0) * quantity } : p);
    const sourceKey = String(g.lineItemId || g.factorId || '');
    const row = {
      id: uuid(),
      category: categoryFor(g.desc),
      description: g.desc,
      quantity,
      unit: g.unit,
      carbon: withTotal(g.carbon),
      bio: withTotal(g.bio),
      weight: withTotal(g.weight),
      locked: true,
      isHeading: false,
      isSubHeading: false,
      heading,
      subHeading,
      sortOrder: g.sortOrder,
      // Load Template keeps the source line-item id (calculator links point at it); a copied-in block
      // gets its own key, so the same assembly added twice can't collide
      factorId: block ? undefined : (g.lineItemId || g.factorId),
      linkKey: block
        ? normKey(`${block.blockId}::${g.heading || ''}::${g.subHeading || ''}::${g.desc}`)
        : (sourceKey || normKey(g.desc)),
      ...(assemblyBlock ? { assemblyBlock } : {}),
    };
    rows.push(row);
    envByKey.set(`${g.heading || ''}::${g.subHeading || ''}::${g.desc}`, row);

    // calculator history: the source's own working, its Factor wrapped by the scale (links dropped —
    // they pointed at the SOURCE's line items, which aren't in this material)
    const saved = parseRows(g.lineItemId ? liById.get(g.lineItemId)?.line_item_rows : null);
    if (saved.length > 0) {
      rowHistory[row.id] = block
        ? saved.map(r => {
          const plain = { ...r };
          delete plain.linkedSourceId;
          const orig = plain.factor == null ? '' : String(plain.factor);
          return materialise({ ...plain, assemblyOrigFactor: orig, factor: multiplyFactor(orig, k) });
        })
        : saved;
    } else if (g.baseQty > 0) {
      rowHistory[row.id] = block
        ? [materialise({
          description: `From ${block.name}`,
          count: '', length: String(g.baseQty), width: '', height: '',
          factor: multiplyFactor('', k), assemblyOrigFactor: '',
        })]
        : [{
          description: g.desc,
          count: '', length: String(g.baseQty), width: '', height: '', factor: '',
          length_evaluated: g.baseQty,
          total: g.baseQty,
        }];
    }
  }

  // ── cost (Rate) rows ──
  const rateByKey = new Map();
  for (const d of data) {
    if (!typeMatchesBase(d.MeasurementType, TYPE_BASE.RATE)) continue;
    const desc = descOf(d);
    if (!desc) continue;
    const { heading, subHeading } = placeOf(d);
    const code = d.MeasurementUnitRateId || d.FactorId || '';
    const key = block ? `${heading || ''}::${subHeading || ''}::${desc}::${code}` : `${desc}::${code}`;
    const prev = rateByKey.get(key) || { desc, heading, subHeading, baseQty: 0, unit: d.QuantityTypeName || d.Unit || '' };
    prev.baseQty += Number(d.Factor || 0);
    prev.rate = {
      measurementId: d.MeasurementType || '',
      code: d.MeasurementUnitRateId || '',
      unit: d.QuantityTypeName || '',
      value: Number(d.Value || 0),
    };
    rateByKey.set(key, prev);
  }
  const costRows = Array.from(rateByKey.values()).map(r => {
    const quantity = r.baseQty * k;
    const env = envByKey.get(`${r.heading || ''}::${r.subHeading || ''}::${r.desc}`);
    return {
      id: uuid(),
      category: categoryFor(r.desc),
      description: r.desc,
      quantity,
      unit: r.unit,
      rate: { ...r.rate, total: (Number(r.rate.value) || 0) * quantity },
      locked: true,
      heading: block ? title : (r.heading || null),
      ...(block && env ? { envRowId: env.id } : {}),
      ...(assemblyBlock ? { assemblyBlock } : {}),
    };
  });

  return { rows, costRows, rowHistory };
}

const inBlock = (r, blockId) => r?.assemblyBlock?.blockId === blockId;

/** Take an added assembly back out: its heading, rows, cost rows and calculator history. */
export function removeAssemblyBlock({ rows, costRows, rowHistory }, blockId) {
  const gone = new Set(rows.filter(r => inBlock(r, blockId)).map(r => r.id));
  const history = { ...rowHistory };
  gone.forEach(id => { delete history[id]; });
  return {
    rows: rows.filter(r => !gone.has(r.id)),
    costRows: costRows.filter(c => !inBlock(c, blockId) && !gone.has(c.envRowId)),
    rowHistory: history,
  };
}

/** Change how much of an added assembly goes in: every quantity × new/old, the heading and working follow. */
export function rescaleAssemblyBlock({ rows, costRows, rowHistory }, blockId, newScale) {
  const head = rows.find(r => r.isHeading && inBlock(r, blockId));
  const oldScale = Number(head?.assemblyBlock?.scale);
  const next = Number(newScale);
  if (!head || !(oldScale > 0) || !(next > 0) || next === oldScale) return { rows, costRows, rowHistory };
  const ratio = next / oldScale;
  const assemblyBlock = { ...head.assemblyBlock, scale: next };
  const title = blockTitle(assemblyBlock.name, next);
  const oldTitle = head.heading;
  const scalePack = (p, qty) => (p && (p.code || p.measurementId) ? { ...p, total: (Number(p.value) || 0) * qty } : p);

  const history = { ...rowHistory };
  const newRows = rows.map(r => {
    if (!inBlock(r, blockId)) return r;
    if (r.isHeading) return { ...r, heading: title, fromAssembly: assemblyBlock, assemblyBlock };
    if (r.isSubHeading) return { ...r, heading: r.heading === oldTitle ? title : r.heading, assemblyBlock };
    const quantity = (Number(r.quantity) || 0) * ratio;
    if (Array.isArray(history[r.id])) {
      history[r.id] = history[r.id].map(c => (c.assemblyOrigFactor !== undefined
        ? materialise({ ...c, factor: multiplyFactor(c.assemblyOrigFactor, next) })
        : c));
    }
    return {
      ...r,
      quantity,
      heading: r.heading === oldTitle ? title : r.heading,
      carbon: scalePack(r.carbon, quantity),
      bio: scalePack(r.bio, quantity),
      weight: scalePack(r.weight, quantity),
      assemblyBlock,
    };
  });
  const newCost = costRows.map(c => {
    if (!inBlock(c, blockId)) return c;
    const quantity = (Number(c.quantity) || 0) * ratio;
    return {
      ...c,
      quantity,
      heading: c.heading === oldTitle ? title : c.heading,
      rate: c.rate ? { ...c.rate, total: (Number(c.rate.value) || 0) * quantity } : c.rate,
      assemblyBlock,
    };
  });
  return { rows: newRows, costRows: newCost, rowHistory: history };
}

/** "Includes: Framing 90×45 (×1), Gib lining 13mm (×2)" — for the request's comments. */
export function includesNote(rows) {
  const seen = new Map();
  (rows || []).forEach(r => {
    if (r.isHeading && r.assemblyBlock) seen.set(r.assemblyBlock.blockId, r.assemblyBlock);
  });
  if (!seen.size) return '';
  return `Includes: ${[...seen.values()].map(b => `${b.name} (×${fmt(Number(b.scale) || 0)})`).join(', ')}`;
}
